// src/components/wall-v2/wallGantt.ts
//
// Geometry for the timeline board: person rows against a shared time axis.
//
// A Gantt on a kitchen wall lives or dies on ONE number — pixels per hour. The
// proposed mockup drew 7a–9p, fourteen hours across ~810px of track: 58px an
// hour, so a one-hour block holds about five characters and every label
// truncates ("Food shop…", "Dinner clea…"). At eight feet a clipped label is
// noise, not information.
//
// Two rules fix that, and they are the whole design:
//
//  1. THE WINDOW ROLLS. The board shows a span around now, not the calendar
//     day. Eight hours instead of fourteen is ~101px an hour — nearly double
//     the room per block — and a wall that advances through the day is more
//     useful than one that keeps rendering breakfast at 4pm. Anything past the
//     right edge is counted, never silently dropped.
//
//  2. A NARROW BLOCK PUTS ITS LABEL OUTSIDE. A 30-minute block is ~50px; no
//     type size makes a word fit there. Rather than clip, the label sits to the
//     right of the bar and the bar keeps its true width, so duration stays
//     honest and the words stay readable.

import type { WallDayData } from '@/hooks/useWallData';
import type { TimelineItem } from '@/types/timeline';
import type { FamilyMember } from '@/types/family';
import type { Task } from '@/types/task';
import { homeworkDue, sortHomework, homeworkOwners } from '@/lib/wall/homeworkLabel';
import { routineEarnsTheWall } from '@/lib/routineUtils';
import { PREVIEW_SECTIONS } from '@/components/wall/today/tomorrowPreview';
import type { DaySection } from '@/lib/timeUtils';
import { HOUSEHOLD_ID, householdMember, titleForMember, isHandoffEvent, withoutMemberList } from './wallEventAttribution';
import { ownersOf } from './wallLanes';

/** Hours of track. Below this a quiet day stretches two items across the wall. */
export const MIN_SPAN_H = 6;
/**
 * Hard cap on the window.
 *
 * This was 8, on the reasoning that a wider window makes blocks too narrow to
 * label. Watching the real wall at 7:57am killed that: the window ran 6a-2p,
 * and Scott's whole evening sat past the right edge as "+2 later" while his
 * row read "Nothing scheduled". Hiding half the day is a worse failure than a
 * narrow bar, and it is no longer the trade it was — a bar too narrow for its
 * label now hands the label to the clear track beside it (see MIN_LABEL_PX),
 * so width costs legibility far more slowly than it used to.
 *
 * 14 hours is a waking day. The window still only opens as far as the day's
 * last item needs, so a genuinely short day still draws a tight board.
 */
export const MAX_SPAN_H = 14;
/** Track width in px at 1024 wide, used to decide if a label fits inside. */
export const TRACK_PX = 780;
/**
 * A bar narrower than this cannot hold readable type at eight feet.
 *
 * Measured, not guessed: labels render at 1.05rem (~17px) with 24px of
 * horizontal padding, so a 101px bar — a one-hour block on an eight-hour
 * window — leaves ~77px, about six characters. "Food shopping" became
 * "GANTT Foo". 170px is roughly twelve characters, which is a real label.
 */
export const MIN_LABEL_PX = 170;
/**
 * Width of the zone column — the row's words that have no place on a clock.
 * Sized for three lines of 0.95rem bold at ~24 characters, which is
 * "Visual Art" or "Math sheet · Fri" with room to spare. Reserved only when
 * some row uses it, so a quiet day keeps the whole track.
 */
export const ZONE_W = 236;
/** Zone lines shown before "and N more". */
export const ZONE_SHOWN = 3;
/**
 * The sections the board reads.
 *
 * PREVIEW_SECTIONS deliberately omits 'unscheduled', because an untimed item
 * can't be "the next thing" in a preview. The board is not a preview — its
 * untimed line is exactly where a task with no clock time belongs, and leaving
 * the section out is part of why rows read empty on a day with real work in
 * them.
 */
const BOARD_SECTIONS: DaySection[] = [...PREVIEW_SECTIONS, 'unscheduled'];

/**
 * A free event this long is not a commitment, it is WHERE SOMEONE IS. School
 * (7:30–2:10) and after-care (2:10–5:30) are the kids' whole day; drawn as
 * dimmed forty-percent bars they read as two things nobody bothered with.
 * Drawn as a stay — a pale filled box with the words inside — they read as
 * the day's shape. Two hours: long enough that a "free" birthday party
 * qualifies, short enough that a fifteen-minute handoff never does.
 */
export const STAY_MIN = 120;

/** Default duration for an item with a start but no end. */
const DEFAULT_DURATION_MIN = 60;

/** True when an item cannot be drawn as a bar: it has no clock time, or is all-day. */
function isAnytimeItem(it: TimelineItem): boolean {
  return !it.startTime || !!it.allDay;
}

export interface GanttBlock {
  id: string;
  title: string;
  /** Percent of the track, 0–100. */
  leftPct: number;
  widthPct: number;
  /**
   * Where the label goes. 'in' when the bar can hold it; otherwise the side
   * with enough clear track before the neighbouring bar. A label placed
   * outside is why the bar can keep its TRUE width — duration stays honest
   * and the words stay readable, instead of trading one for the other.
   */
  labelSide: 'in' | 'right' | 'left';
  /** Room available to an outside label, as a percent of the track. */
  labelRoomPct: number;
  /** Already finished — drawn dimmer, because it is context, not a commitment. */
  past: boolean;
  /** Informational-only: no prep/handoff expected — drawn dimmer with a "Free" label. */
  free?: boolean;
  /** A long free event: where this person IS. Pale box, label inside, not dimmed. */
  stay?: boolean;
  /**
   * A handoff (walk / pick up / drop off …) nobody has claimed, so it sits on
   * the household row. Tapping it is how the board asks "who?".
   */
  openHandoff?: boolean;
  type: TimelineItem['type'];
}

/** One open homework item, as the row draws it. */
export interface GanttHomework {
  id: string;
  /** "Blue sheet · Fri" — title plus the due word, or the title alone when undated. */
  label: string;
  late: boolean;
}

/** One line in a row's zone. */
export interface GanttZoneItem {
  id: string;
  title: string;
  /** 'special' = all-day event (Specials, Picture Day, Labor Day); 'routine' = untimed rare routine. */
  kind: 'special' | 'routine';
}

export interface GanttTrack {
  memberId: string;
  name: string;
  blocks: GanttBlock[];
  /**
   * Open homework for this person, from the day it arrives until it is
   * checked off. A separate array from `anytime` so the day's chips keep
   * their own cap and time order; homework has neither a time nor a day —
   * it has a due date, and it nags until done.
   */
  homework: GanttHomework[];
  /**
   * What the row says beside the name, off the axis: today's special (an
   * all-day event) and any untimed routine rare enough to be news. In section
   * order — all-day first, then the day. An untimed TASK is not here: the
   * strip's "Due today" card lists it and the person's page holds it.
   */
  zone: GanttZoneItem[];
  /** Items that start after the window closes — counted, not dropped. */
  laterCount: number;
  /** Something happening on this row RIGHT NOW that has no calendar slot —
   *  a reading timer running. Set by the shell, not the adapter. */
  live?: string;
}

export interface GanttAxis {
  /** Minutes since local midnight. */
  startMin: number;
  endMin: number;
  ticks: { min: number; label: string; leftPct: number }[];
  /** Position of the current moment, or null when now is off the board. */
  nowPct: number | null;
}

export interface GanttBoard {
  axis: GanttAxis;
  tracks: GanttTrack[];
  /** Px the tracks give up to the zone column; 0 when no row uses it. */
  zoneW: number;
}

/**
 * Find a block's title by id, across every track.
 *
 * Belt-and-braces for WallV2Shell's tap handler: the primary lookup
 * (adaptTimelineSections' output) should always carry the same id a board
 * bar draws with, but if that ever drifts out of sync again, this recovers a
 * human-readable label from the board data already on screen — so the tap
 * flashes the item's title instead of dead-ending silently.
 *
 * This already does real work today, not just defensively: adaptTimelineSections'
 * dedupeRoutines collapses two same-title routines (e.g. two kids' "Get
 * undressed") onto ONE id, but this board draws a bar per instance with no
 * such dedupe — so the second kid's bar has an id `adaptTimelineSections`
 * never carries, and this is what turns that tap into a title flash instead
 * of a dead end.
 */
export function titleForBlockId(board: GanttBoard, itemId: string): string | null {
  for (const track of board.tracks) {
    const block = track.blocks.find((b) => b.id === itemId);
    if (block) return block.title;
  }
  return null;
}

function minutesOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

function hourLabel(min: number): string {
  const h24 = Math.floor(min / 60) % 24;
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}${h24 < 12 ? 'a' : 'p'}`;
}

/**
 * The visible span. Anchored one hour before now so the thing just finished
 * stays on screen, then widened to cover what is still ahead — up to the point
 * where blocks stop being labellable.
 */
export function computeAxis(itemStarts: number[], itemEnds: number[], now: Date): GanttAxis {
  const nowMin = minutesOfDay(now);
  const startMin = Math.max(0, Math.floor((nowMin - 60) / 60) * 60);

  const lastEnd = itemEnds.length ? Math.max(...itemEnds) : startMin;
  const needed = Math.ceil((lastEnd - startMin) / 60);
  const spanH = Math.min(MAX_SPAN_H, Math.max(MIN_SPAN_H, needed));
  const endMin = Math.min(24 * 60, startMin + spanH * 60);

  const span = endMin - startMin;
  const ticks: GanttAxis['ticks'] = [];
  // Every second hour: an hourly ruler at this density is more ink than signal.
  const step = spanH > 6 ? 120 : 60;
  for (let m = startMin; m <= endMin; m += step) {
    ticks.push({ min: m, label: hourLabel(m), leftPct: ((m - startMin) / span) * 100 });
  }

  const nowPct = nowMin >= startMin && nowMin <= endMin
    ? ((nowMin - startMin) / span) * 100
    : null;

  // itemStarts is unused for sizing today but kept in the signature so a future
  // "tighten to content on a quiet day" rule has what it needs.
  void itemStarts;
  return { startMin, endMin, ticks, nowPct };
}

/**
 * Which row draws an item.
 *
 * The lanes' rule (`ownersOf`) drops an unassigned task or routine on the
 * floor, so that a chore can never headline a person in the wall's largest
 * type. A board row is not a headline — every bar is the same size — so the
 * rule that protects a lane just empties a board. Two differences here:
 *
 *  - An unassigned task or routine falls to the household row rather than
 *    vanishing. An EVENT that matched nobody does not: `attributeEvent`
 *    returns [] only for calendars deliberately kept off the wall (holidays,
 *    the meal calendar the dinner card already draws), and resurrecting those
 *    would undo a decision, not fix a gap.
 *
 *  - Rhythm routines never reach here: `itemsFor` drops what does not earn
 *    the wall (see `routineEarnsTheWall`).
 */
export function boardOwnersOf(item: TimelineItem, members: FamilyMember[]): string[] {
  const owners = ownersOf(item, members);
  if (owners.length > 0) return owners;
  return item.type === 'event' ? [] : [HOUSEHOLD_ID];
}

function itemsFor(day: WallDayData, memberId: string, members: FamilyMember[]): TimelineItem[] {
  const out: TimelineItem[] = [];
  for (const section of BOARD_SECTIONS) {
    for (const item of day.items[section] ?? []) {
      // A collection step (e.g. one exercise in "Camp Mornings") never draws
      // its own bar or anytime chip. The wall has no collection renderer to
      // hand it to, so a step is DROPPED here, not relocated to its parent —
      // without this, a step whose own recurrence isn't "everyday" slips past
      // isAnytimeItem and draws as a real timed bar.
      if (item.type === 'routine' && item.originalRoutine?.parent_routine_id != null) continue;
      // The wall shows what is unusual about today. A routine that runs most
      // days is the week's shape, not news — it never draws on any row,
      // Everyone included. Two days a week or rarer earns its place.
      if (item.type === 'routine' && !routineEarnsTheWall(item.recurrencePattern)) continue;
      if (!boardOwnersOf(item, members).includes(memberId)) continue;
      // Done means gone — for a commitment. An all-day rotation ("Specials —
      // Ella: PE") or a free stay is information, and "did you have gym
      // today" has the same answer after someone taps it done. 2026-09-03:
      // today's Specials instance was marked completed and both kids' chips
      // vanished from the wall.
      if (item.completed && !item.allDay && !item.isFree) continue;
      out.push(item);
    }
  }
  return out;
}

/**
 * The board. Today only — a time axis across more than one day is a calendar,
 * and the wall already has one of those.
 */
export function adaptGanttBoard(
  members: FamilyMember[],
  days: WallDayData[],
  now: Date,
  trackPx: number = TRACK_PX,
  homework: Task[] = [],
): GanttBoard {
  const today = days[0];
  const roster = [...members, householdMember()];

  // Homework by row. Every member it is assigned to gets it on their row;
  // anyone else (unassigned, or member ids the wall doesn't know) → the
  // household row, the same fall-through boardOwnersOf gives an unassigned
  // task.
  //
  // `assignedToAll` is read FIRST and is why one row can sit on two kids'
  // rows. Class-wide homework used to be written as one row per child purely
  // so each kid saw it as theirs here; with two kids in mirror-image classes
  // that put every class-wide instruction on Today twice. The pipeline now
  // writes ONE row carrying both kids, which only works if this reads it —
  // otherwise a merged row lands on one kid's row, or falls to Everyone,
  // which is exactly the 2026-09-03 defect that fan-out was fixing.
  const memberIds = new Set(members.map((m) => m.id));
  const homeworkByTrack = new Map<string, Task[]>();
  for (const t of homework) {
    if (t.completed) continue;
    // Nothing recognisable → the household row.
    const owners = homeworkOwners(t, memberIds);
    if (!owners.length) owners.push(HOUSEHOLD_ID);
    for (const key of owners) {
      const list = homeworkByTrack.get(key);
      if (list) list.push(t);
      else homeworkByTrack.set(key, [t]);
    }
  }

  const starts: number[] = [];
  const ends: number[] = [];
  if (today) {
    for (const m of roster) {
      for (const it of itemsFor(today, m.id, members)) {
        if (isAnytimeItem(it)) continue;
        starts.push(minutesOfDay(it.startTime!));
        ends.push(
          it.endTime ? minutesOfDay(it.endTime) : minutesOfDay(it.startTime!) + DEFAULT_DURATION_MIN,
        );
      }
    }
  }

  const axis = computeAxis(starts, ends, now);
  const span = axis.endMin - axis.startMin;
  const nowMin = minutesOfDay(now);

  type Timed = { it: TimelineItem; title: string; s: number; e: number };
  // First pass: sort each row's items into the zone (no clock position) and
  // the timed list. The zone column is reserved board-wide or not at all —
  // every track must start at the same x for the ruler to mean anything — so
  // label fitting has to wait until every row has declared what it holds.
  const collected = roster.map((m) => {
    const zone: GanttZoneItem[] = [];
    /** Timed items, collected before drawing so stays can be known first. */
    const timed: Timed[] = [];
    for (const it of today ? itemsFor(today, m.id, members) : []) {
      // One calendar row can carry the whole family's rotation — "Specials —
      // Ella: Visual Art · Kaleb: PE". Attribution rightly puts it in both
      // kids' tracks; rendering the same string in both is what made it
      // useless. Each track shows only the words addressed to that person.
      // …and on a person's own row a trailing name list is the row saying
      // its own name: "School — Ella & Kaleb" is "School" under Ella's face.
      const title = m.id === HOUSEHOLD_ID
        ? titleForMember(it.title, m.name)
        : withoutMemberList(titleForMember(it.title, m.name), members);
      if (isAnytimeItem(it)) {
        // An untimed task has no place on a clock and no place on this board:
        // the strip's "Due today" card lists it, and the person's page holds
        // it under Chores. A row says only what makes today different.
        if (it.type === 'task') continue;
        zone.push({ id: it.id, title, kind: it.type === 'routine' ? 'routine' : 'special' });
        continue;
      }
      const s = minutesOfDay(it.startTime!);
      const e = it.endTime ? minutesOfDay(it.endTime) : s + DEFAULT_DURATION_MIN;
      timed.push({ it, title, s, e });
    }
    return { m, zone, timed };
  });

  const zoneW = collected.some(({ m, zone }) =>
    zone.length > 0 || (homeworkByTrack.get(m.id)?.length ?? 0) > 0) ? ZONE_W : 0;
  const fitPx = trackPx - zoneW;

  const tracks: GanttTrack[] = [];
  for (const { m, zone, timed } of collected) {
    const blocks: GanttBlock[] = [];
    let laterCount = 0;

    // Stays first, so a free scrap inside one can be dropped: "Ella & Kaleb
    // to FFG", fifteen free minutes at 2:10, sits entirely inside the FFG
    // block that already says where they are. Drawing both put a bar with no
    // room for a label on top of the one with the words.
    const stays = timed.filter((t) => !!t.it.isFree && t.e - t.s >= STAY_MIN);
    const insideAStay = (t: { s: number; e: number }) =>
      stays.some((st) => st !== t && st.s <= t.s && t.e <= st.e);

    for (const t of timed) {
      const { it, title, s, e } = t;
      const stay = stays.includes(t);
      if (!stay && it.isFree && insideAStay(t)) continue;

      if (s >= axis.endMin) { laterCount++; continue; }
      if (e <= axis.startMin) continue; // already over and off the left edge

      // Clamp to the window so a block that straddles an edge still reads as
      // running, rather than being dropped or drawn off-canvas.
      const cs = Math.max(s, axis.startMin);
      const ce = Math.min(e, axis.endMin);
      const widthPct = ((ce - cs) / span) * 100;

      blocks.push({
        id: it.id,
        title,
        leftPct: ((cs - axis.startMin) / span) * 100,
        widthPct,
        labelSide: 'in',
        labelRoomPct: 0,
        past: e <= nowMin,
        free: it.isFree,
        stay: stay || undefined,
        openHandoff:
          m.id === HOUSEHOLD_ID && it.type === 'event' && !it.assignedTo && isHandoffEvent(it.title)
            ? true
            : undefined,
        type: it.type,
      });
    }

    blocks.sort((a, b) => a.leftPct - b.leftPct);

    // Label placement needs every block's neighbours, so it runs after sorting.
    //
    // A gap can hold ONE label. Without reserving them, block A writes into the
    // gap on its right while block B writes into the same gap on its left, and
    // the two collide mid-air — "Free plaGANTT Piano practice". So walk left to
    // right and mark each gap as it is claimed.
    //
    // gaps[i] is the clear track immediately BEFORE block i; gaps[n] is the
    // run from the last block to the end of the track.
    const pxOf = (pct: number) => (pct / 100) * fitPx;
    const gaps: number[] = [];
    for (let i = 0; i <= blocks.length; i++) {
      const from = i === 0 ? 0 : blocks[i - 1].leftPct + blocks[i - 1].widthPct;
      const to = i === blocks.length ? 100 : blocks[i].leftPct;
      gaps.push(Math.max(0, to - from));
    }
    // A gap holds one label — unless it is wide enough for two, in which case
    // the block on each side takes half. Without sharing, the second bar in a
    // row kept falling back inside and truncating even though 400px of empty
    // track sat right beside it.
    const claims = new Array<number>(gaps.length).fill(0);
    const roomIn = (g: number, shares: number) => gaps[g] / shares;
    const canClaim = (g: number) =>
      pxOf(roomIn(g, claims[g] + 1)) >= MIN_LABEL_PX;

    for (let i = 0; i < blocks.length; i++) {
      const b = blocks[i];
      if (pxOf(b.widthPct) >= MIN_LABEL_PX) continue;

      if (canClaim(i + 1)) {
        b.labelSide = 'right';
        claims[i + 1] += 1;
      } else if (canClaim(i)) {
        b.labelSide = 'left';
        claims[i] += 1;
      }
      // Neither side has room: the label stays in and truncates. Unavoidable
      // when short blocks sit back to back, and rare enough to accept.
    }

    // Room is divided only once every claim on a gap is known.
    for (let i = 0; i < blocks.length; i++) {
      const b = blocks[i];
      if (b.labelSide === 'right') b.labelRoomPct = roomIn(i + 1, claims[i + 1]);
      else if (b.labelSide === 'left') b.labelRoomPct = roomIn(i, claims[i]);
    }

    const homework = sortHomework(homeworkByTrack.get(m.id) ?? [], now).map((t) => {
      const due = homeworkDue(t.neededOn, now);
      return { id: t.id, label: due.label ? `${t.title} · ${due.label}` : t.title, late: due.late };
    });

    // The household row is a home for what belongs to nobody in particular.
    // A home with nothing in it is a row that says "Nothing scheduled" under
    // a house icon, and on most days that was the wall's last line. Omit it,
    // and the people's rows get the height.
    if (m.id === HOUSEHOLD_ID && blocks.length === 0 && zone.length === 0 && homework.length === 0 && laterCount === 0) continue;

    tracks.push({ memberId: m.id, name: m.name, blocks, homework, zone, laterCount });
  }

  return { axis, tracks, zoneW };
}
